import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Group,
  NumberInput,
  Select,
  Table,
  Text,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons";
import { useServerApi } from "../hooks/userServerApi";
import { useFormatter } from "../hooks/useFomatter";
import {
  collectTrackPointTypes,
  conditionItemLabel,
  idOf,
  pointTypeLabel,
  rankOptions,
} from "../helpers/promotionProgress";

const emptyDownline = (rankCode = "") => ({
  rankCode,
  active: true,
});

const formatAmount = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "-";
  return amount.toLocaleString();
};

const PromotionTrackTester = ({ form }) => {
  const [api] = useServerApi();
  const [formatter] = useFormatter();
  const ranks = form?.values?.ranks || [];
  const rankData = useMemo(() => rankOptions({ ranks }), [ranks]);
  const pointTypes = useMemo(
    () => collectTrackPointTypes({ ranks }),
    [ranks]
  );
  const [sample, setSample] = useState({
    currentRankCode: ranks[0]?.code || "",
    pointBalances: [],
    downlines: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    setSample((current) => {
      const nextBalances = pointTypes.map((pointType) => {
        const id = idOf(pointType);
        const existing = (current.pointBalances || []).find(
          (row) => idOf(row.pointType) === id
        );
        return {
          pointType,
          amount: existing ? existing.amount : 0,
        };
      });
      return { ...current, pointBalances: nextBalances };
    });
  }, [pointTypes.map((item) => idOf(item)).join("|")]);

  const updateSample = (field, value) => {
    setSample((current) => ({ ...current, [field]: value }));
  };

  const updatePointAmount = (pointType, amount) => {
    const id = idOf(pointType);
    setSample((current) => ({
      ...current,
      pointBalances: (current.pointBalances || []).map((row) =>
        idOf(row.pointType) === id ? { ...row, amount } : row
      ),
    }));
  };

  const handleCalculate = async () => {
    setLoading(true);
    setError(null);
    try {
      const preview = await api.previewPromotionTrack({
        track: form?.values || {},
        sample: {
          ...sample,
          pointBalances: (sample.pointBalances || []).map((row) => ({
            pointType: idOf(row.pointType) || row.pointType,
            amount: row.amount,
          })),
        },
      });
      setResult(preview);
    } catch (err) {
      setResult(null);
      setError(err?.message || "Failed to preview promotion");
    } finally {
      setLoading(false);
    }
  };

  const progressItems = result?.progress?.items || result?.steps?.[0]?.items || [];

  return (
    <>
      <Text size="sm" color="dimmed" mb="md">
        Score a sample consultant against the unsaved track. Nothing is written
        to users or overriding plans.
      </Text>
      <Select
        label="Current rank"
        data={rankData}
        value={sample.currentRankCode || null}
        onChange={(value) => updateSample("currentRankCode", value)}
        searchable
        mb="md"
      />
      {pointTypes.length ? (
        <Group grow mb="md" align="flex-end">
          {pointTypes.map((pointType) => {
            const id = idOf(pointType);
            const row = (sample.pointBalances || []).find(
              (item) => idOf(item.pointType) === id
            );
            return (
              <NumberInput
                key={id}
                label={pointTypeLabel(pointType)}
                min={0}
                parser={formatter.numberParser}
                formatter={formatter.numberFormat}
                value={row?.amount || 0}
                onChange={(value) => updatePointAmount(pointType, value)}
              />
            );
          })}
        </Group>
      ) : (
        <Text size="sm" color="dimmed" mb="md">
          Add a point-balance condition with a Point Type to sample balances.
        </Text>
      )}

      <Text size="sm" weight={600} mb="xs">
        Direct downlines
      </Text>
      {(sample.downlines || []).map((row, index) => (
        <Group key={index} mb="xs" align="flex-end">
          <Select
            label={index === 0 ? "Rank" : ""}
            data={rankData}
            value={row.rankCode || null}
            onChange={(value) => {
              const next = [...sample.downlines];
              next[index] = { ...next[index], rankCode: value };
              updateSample("downlines", next);
            }}
            style={{ minWidth: 220 }}
          />
          <Checkbox
            label="Active"
            checked={row.active !== false}
            mb={8}
            onChange={(event) => {
              const next = [...sample.downlines];
              next[index] = {
                ...next[index],
                active: event.currentTarget.checked,
              };
              updateSample("downlines", next);
            }}
          />
          <Button
            size="xs"
            color="red"
            variant="subtle"
            leftIcon={<IconTrash size={14} />}
            mb={4}
            onClick={() =>
              updateSample(
                "downlines",
                sample.downlines.filter((_, rowIndex) => rowIndex !== index)
              )
            }
          >
            Remove
          </Button>
        </Group>
      ))}
      <Button
        size="xs"
        variant="light"
        leftIcon={<IconPlus size={14} />}
        mb="md"
        onClick={() =>
          updateSample("downlines", [
            ...(sample.downlines || []),
            emptyDownline(ranks[0]?.code),
          ])
        }
      >
        Add downline
      </Button>

      <Group mb="md">
        <Button onClick={handleCalculate} loading={loading}>
          Preview
        </Button>
      </Group>

      {error && (
        <Alert color="red" mb="md">
          {error}
        </Alert>
      )}

      {result && (
        <>
          <Text size="sm" mb="sm">
            {result.fromRankCode || "-"} {result.promoted ? "promotes to" : "stays"}{" "}
            <Text span weight={700}>
              {result.toRankCode || "-"}
            </Text>
            {result.progress?.logic ? (
              <Text span size="xs" color="dimmed">
                {" "}
                ({result.progress.logic === "OR" ? "any condition" : "all conditions"})
              </Text>
            ) : null}
          </Text>
          <Table>
            <thead>
              <tr>
                <th>Condition</th>
                <th>Required</th>
                <th>Actual</th>
                <th>Pass</th>
              </tr>
            </thead>
            <tbody>
              {progressItems.map((item, index) => (
                <tr key={index}>
                  <td>{conditionItemLabel(item)}</td>
                  <td>{formatAmount(item.required)}</td>
                  <td>{formatAmount(item.actual)}</td>
                  <td>{item.passed ? "Yes" : "No"}</td>
                </tr>
              ))}
              {!progressItems.length && (
                <tr>
                  <td colSpan={4}>No conditions on the current rank.</td>
                </tr>
              )}
            </tbody>
          </Table>
        </>
      )}
    </>
  );
};

export default PromotionTrackTester;
